/**
 * 앱이 **뜨지 못한 자리**의 복구 판정 — 순수 함수 (2026-09-03 리뷰 지적 · 12R P9).
 *
 * `main.js` 는 첫 라우트가 정해진 뒤에 앱을 붙인다(`router.onReady`). 그런데 vue-router 3 의 `onReady(cb, errorCb)`
 * 는 최초 내비게이션이 **성공했을 때만** `cb` 를 부른다 — 실패하면 `readyErrorCbs` 만 돌고 `ready` 는 계속 false 라
 * `cb` 는 영영 오지 않는다. 그래서 오류 콜백이 없으면 `#app` 이 빈 채로 남는다: 오류 화면도, 로그인 화면도,
 * 되돌아갈 길도 없는 **완전한 백지**다. POS 임베드 창에서는 그것이 곧 이탈이고 매직링크 토큰은 이미 소비돼 있다.
 *
 * 방아쇠는 실재한다 — 임베드 라우트는 지연 청크(`() => import(...)`)이고, 배포 직후 옛 해시의 청크를 요청하면
 * Firebase Hosting 의 SPA rewrite 가 `index.html` 을 돌려주므로 그 응답이 스크립트로 해석되지 않아 거부된다.
 *
 * ★ **실패한 자리에 앱을 그냥 붙이지 않는다** (12R P9 — 8R~12R 다섯 번 재지적). 붙여도 라우터는 아직 START 라
 * `router-view` 가 비고, `/v/pos/*` 는 셸 없는 `EmbedLayout` 이라 **백지가 그대로**다. 재시도가 불가하거나 청크가 아닌
 * 오류면 라우터·지연 청크에 기대지 않는 **정적 복구 화면**([BOOT_ACTION.FALLBACK])이 답이다.
 */

/** 한 번만 새로고침한다는 표시 — 무한 새로고침이 백지보다 나쁘다 */
export const BOOT_RELOAD_ONCE_KEY = 'ordern_boot_reload_once'

/**
 * 최초 내비게이션 실패에 대한 답의 어휘.
 * - `RELOAD` — 지연 청크를 못 받았고 아직 새로고침한 적 없다: 한 번만 새로고침한다.
 * - `FALLBACK` — 그 밖 전부: 정적 복구 화면(`views/BootFailureView.vue`)을 그린다.
 */
export const BOOT_ACTION = Object.freeze({ RELOAD: 'reload', FALLBACK: 'fallback' })

/**
 * 지연 청크를 못 받아 실패했는가.
 *
 * webpack 은 `ChunkLoadError`(`Loading chunk N failed`)를, 스크립트가 아닌 응답을 받은 파서는 `SyntaxError` 를 낸다.
 * 둘 다 **다시 받으면 풀리는** 실패라 한 번의 새로고침이 답이고, 그 밖의 실패(라우터 가드 오류 등)는 새로고침해도
 * 같은 결과라 복구 화면이 답이다.
 */
export function isChunkLoadFailure(error) {
  if (!error) return false
  const name = String(error.name || '')
  const message = String(error.message || error)
  if (name === 'ChunkLoadError') return true
  return /loading (css )?chunk .* failed/i.test(message) || /unexpected token '</i.test(message)
}

/**
 * 최초 내비게이션이 실패했을 때 무엇을 할 것인가.
 *
 * @returns {'reload'|'fallback'} [BOOT_ACTION] — 청크 실패이고 아직 새로고침하지 않았으면 `reload`, 이미 한 번 새로고침
 *   했거나(재시도 불가) 청크가 아닌 오류(오류를 모르는 경우 포함)면 `fallback`.
 */
export function bootFailureAction(error, alreadyReloaded) {
  return isChunkLoadFailure(error) && !alreadyReloaded ? BOOT_ACTION.RELOAD : BOOT_ACTION.FALLBACK
}
